import { Component, OnDestroy, OnInit } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';

@Component({
	selector: 'app-lift-chart',
	styleUrls: ['./lift-chart.component.css'],
	templateUrl: './lift-chart.component.html'
})
export class LiftChartComponent implements OnInit, WidgetCore, OnDestroy {
	public sectiontitle: string;
	public key: string;
	public timeserieskey: string;
	public placeholderimage: string;
	public campaignsArray = [];
	public showLoad = true;
	public colorPalette: ColorPalette;
	public reloadSubscription;
	public widgetDataSubscription;
	public widgetTimelineSubscription;
	private campaignsList = [];

	constructor(
		private analyticsService: AnalyticsService,
		private colorUtilityService: ColorUtilityService,
		private analyticsDataModel: AnalyticsDataModel
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
	}

	ngOnInit(): void {
		this.requestData();
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
		if (this.widgetTimelineSubscription) {
			this.widgetTimelineSubscription.unsubscribe();
		}
	}

	requestData(): void {
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((res: any) => {
			if (res.success) {
				this.campaignsList = res.data;
			}
			this.widgetTimelineSubscription = this.analyticsService
				.processWidgetRequest(this.timeserieskey)
				.subscribe((response: any) => {
					this.showLoad = false;
					if (response.success) {
						this.campaignsList.map((campaignItem) => {
							campaignItem.data = response.data.filter((obj) => obj.campaign_id === campaignItem.campaign_id);
						});
					} else {
						this.campaignsList = [];
					}
					this.processResponseData();
					this.campaignsArray = this.campaignsList;
				});
		});
	}

	processResponseData(): void {
		if (!this.campaignsList.length) {
			return;
		}
		this.campaignsList = this.campaignsList.map((campaignItem, index) => {
			const campaign = {
				average_baseline: campaignItem.average_baseline,
				average_exposed_rate: campaignItem.average_conversion_rate,
				average_lift: campaignItem.average_lift,
				graphData: [],
				id: campaignItem.campaign_id,
				legendLayout: {},
				name: campaignItem.campaign_name,
				subtitle: {},
				title: {},
				xAxis: {}
			};
			const baseline_data = [];
			const exposed_rate = [];
			const lift_data = [];
			const categories = [];
			const xAxisObj: any = {};
			campaignItem.data.forEach((dataObj) => {
				baseline_data.push(Number(dataObj.average_baseline));
				exposed_rate.push(Number(dataObj.average_conversion_rate));
				lift_data.push(this.buildLiftDataObject(dataObj));
				categories.push(dataObj.impression_date);
			});
			xAxisObj.labels = { rotation: -45 };
			xAxisObj.categories = categories;
			campaign.xAxis = xAxisObj;
			campaign.graphData = [
				{
					color: this.colorUtilityService.getRGBByColorType(this.colorPalette, 'primary', 1),
					data: baseline_data,
					fillColor: this.colorUtilityService.getRGBByColorType(this.colorPalette, 'primary', 0.3),
					name: 'Baseline Rate',
					tooltip: {
						pointFormat: '{series.name}: <b>{point.y}%</b>'
					},
					type: 'area'
				},
				{
					color: this.colorUtilityService.getRGBByColorType(this.colorPalette, 'secondary', 1),
					data: exposed_rate,
					fillColor: this.colorUtilityService.getRGBByColorType(this.colorPalette, 'secondary', 0.3),
					name: 'Exposed Rate',
					tooltip: {
						pointFormat: '{series.name}: <b>{point.y}%</b>'
					},
					type: 'area'
				},
				{
					// borderColor: '#D97F4B',
					// borderWidth: 2,
					color: this.colorUtilityService.getRGBByColorType(this.colorPalette, 'tertiary', 1),
					data: lift_data,
					name: 'Lift',
					tooltip: {
						pointFormat: '{series.name}: <b>{point.displayLift}x</b>'
					},
					type: 'column',
					yAxis: 1
				}
			];
			return campaign;
		});
		this.configGraphOptions();
	}

	buildLiftDataObject(dataObject: any): any {
		const liftTempObj: any = {};
		if (Number(dataObject.average_lift) > 99) {
			liftTempObj.y = 99;
			liftTempObj.displayLift = '>99';
		} else {
			liftTempObj.y = Number(dataObject.average_lift);
			liftTempObj.displayLift = dataObject.average_lift;
		}
		return liftTempObj;
	}

	public configGraphOptions(): void {
		if (this.campaignsList.length > 1) {
			this.campaignsList.forEach((campaign, index) => {
				campaign.legendLayout = {
					align: 'right',
					floating: true,
					layout: 'vertical',
					symbolHeight: 7,
					symbolWidth: 7,
					verticalAlign: 'top',
					y: -15
				};
				campaign.title = {
					align: 'left',
					margin: 50,
					style: {
						color: '#213038',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap'
					},
					text: campaign.name,
					useHTML: true,
					widthAdjust: -100
				};
				campaign.subtitle = {
					align: 'left',
					style: {
						color: '#5B7D8D',
						fontSize: '10px'
					},
					text:
						// tslint:disable-next-line: prefer-template
						'<b>Lift: </b>' +
						(Number(campaign.average_lift) > 99 ? '>99' : Number(campaign.average_lift)) +
						'x  ' +
						' <b>Exposed Rate: </b>' +
						campaign.average_exposed_rate +
						'%    <b>Baseline Rate: </b>' +
						campaign.average_baseline +
						'%',
					useHTML: true
				};
			});
		} else {
			this.campaignsList.forEach((campaign) => {
				campaign.legendLayout = {
					align: 'right',
					layout: 'vertical',
					symbolHeight: 7,
					symbolWidth: 7,
					verticalAlign: 'middle',
					y: -100
				};
				campaign.title = {
					text: null
				};
				campaign.subtitle = {
					text: null
				};
			});
		}
	}
}
